import { z } from 'zod'

// ── Portal Colors (shared between storage and render) ────────────────

const PortalColorsSchema = z.object({
  primary: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  background: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  foreground: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  cardBackground: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  cardForeground: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  button: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
})

export type PortalColors = z.infer<typeof PortalColorsSchema>

// ── Customer Theme (storage/input) ───────────────────────────────────

export const CustomerThemeInputSchema = z.object({
  slug: z.string().regex(/^[a-z0-9-]+$/),
  validityDays: z.number().int().positive().nullable().optional(),
  portalColors: PortalColorsSchema.nullable().optional(),
  fontFamily: z.string().nullable().optional(),
  fontFiles: z
    .array(z.object({ key: z.string(), weight: z.string() }))
    .nullable()
    .optional(),
  logoUrl: z.string().nullable().optional(),
  backgroundUrl: z.string().nullable().optional(),
})

export type CustomerThemeInput = z.infer<typeof CustomerThemeInputSchema>

/** Shape of the admin theme-editor form (react-hook-form + zodResolver) */
export const ThemeEditorFormSchema = z.object({
  slug: z
    .string()
    .min(1, 'URL slug is required')
    .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers and dashes'),
  portalColors: z.object({
    primary: z.string(),
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    button: z.string(),
  }),
  fontFamily: z.string(),
  logoUrl: z.string(),
  backgroundUrl: z.string(),
  validityDays: z.number().nullable(),
})

export type ThemeEditorFormValues = z.infer<typeof ThemeEditorFormSchema>

/** Shape of the config JSON column in customer_theme table */
export const CustomerThemeConfigSchema = z.object({
  portalColors: PortalColorsSchema.optional(),
  fontFamily: z.string().optional(),
})

export type CustomerThemeConfig = z.infer<typeof CustomerThemeConfigSchema>

const FontFileSchema = z.object({ key: z.string(), weight: z.string() })

/** Zod schema for a customer_theme DB record */
export const CustomerThemeRecordSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  customerId: z.string(),
  config: CustomerThemeConfigSchema,
  logoUrl: z.string().nullable(),
  backgroundUrl: z.string().nullable(),
  fontFiles: z.array(FontFileSchema).nullable(),
  validityDays: z.number().nullable(),
  isPublished: z.boolean(),
})

export type CustomerThemeRecord = z.infer<typeof CustomerThemeRecordSchema>

/** Parse a raw Prisma customer_theme row into typed record */
export function parseCustomerTheme(raw: {
  id: string
  name: string
  slug: string
  customerId: string
  config: unknown
  logoUrl: string | null
  backgroundUrl: string | null
  fontFiles: unknown
  validityDays: number | null
  isPublished: boolean
}): CustomerThemeRecord {
  return CustomerThemeRecordSchema.parse({
    ...raw,
    config: raw.config || {},
    fontFiles: raw.fontFiles || null,
  })
}

// ── Portal Theme (runtime/render) ────────────────────────────────────

export const PortalThemeSchema = z.object({
  colors: z.object({
    primary: z.string(),
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    button: z.string(),
  }),
  logoUrl: z.string().nullable(),
  backgroundUrl: z.string().nullable(),
  fontFamily: z.string(),
  fontFiles: z.array(z.object({ url: z.string(), weight: z.string() })),
})

export type PortalTheme = z.infer<typeof PortalThemeSchema>

export const DEFAULT_PORTAL_THEME: PortalTheme = {
  colors: {
    primary: '#1a1a2e',
    background: '#f5f5f5',
    foreground: '#1a1a2e',
    cardBackground: '#ffffff',
    cardForeground: '#666666',
    button: '#1a1a2e',
  },
  logoUrl: null,
  backgroundUrl: null,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontFiles: [],
}

// ── Gift Items & Validation ──────────────────────────────────────────

export const GiftItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  picture: z.string().nullable(),
  price: z.number().nullable(),
  // reason: kept as a plain string rather than the incentive-type enum because
  // IncentiveItem.type is an unconstrained Postgres String — an unexpected
  // value must still render the portal, not fail schema parsing.
  type: z.string(),
})

export type GiftItem = z.infer<typeof GiftItemSchema>

// ── Shipping address ─────────────────────────────────────────────────
//
// One owner for the whole concept: the shape, the coercion from untyped
// input, and the display format. The claim request, the stored JSON,
// the uploaded address, the portal views, the email and the CSV export all
// go through here. The export also labels one column per field, and a field
// without a label there fails to compile.

/** A blank string is the same as no value: an untouched input still posts ''. */
const OptionalField = z.preprocess((v) => {
  if (typeof v === 'number') return String(v)
  if (typeof v !== 'string') return undefined
  return v.trim() || undefined
}, z.string().optional())

/**
 * An address as it exists *on file* — nothing required.
 *
 * This is the base shape and the only place the field list is written down.
 *
 * Nothing is required because the uploaded `EndUserCampaign.address` is
 * whatever a CSV import happened to carry, and it is routinely partial: it
 * has no `name` key at all (the import keeps names in their own columns) and
 * `end-users-upload.schema.ts` even allows a **numeric** postcode. Parsing
 * that with the strict shape below would fail and throw away a street, city
 * and postcode we do know. It is the claim, not the prefill, that enforces
 * the required fields.
 */
export const AddressOnFileSchema = z.object({
  name: OptionalField,
  postcode: OptionalField,
  houseNumber: OptionalField,
  streetName: OptionalField,
  city: OptionalField,
  country: OptionalField,
})

export type AddressOnFile = z.infer<typeof AddressOnFileSchema>

/**
 * A delivery address complete enough to ship to — what gets stored on a
 * claim, and what the recipient confirmed.
 *
 * Derived from the on-file shape by requiring two of its fields, so the
 * field list stays in one place. Note the direction: the strict shape
 * extends the loose one, rather than the loose one being
 * `ShippingAddressSchema.partial()` — `.partial()` keeps the inner `min(1)`
 * and would reject a half-filled address on file.
 *
 * Only `name` and `postcode` are required: for a Dutch address the postcode
 * is what carries the delivery, and this matches what the offline end-user
 * upload already demands (`firstName` plus `address.postcode`, in
 * `end-users-upload.schema.ts`). Required-field validation only; nothing is
 * checked against a postal database.
 */
export const ShippingAddressSchema = AddressOnFileSchema.extend({
  name: z.string().trim().min(1),
  postcode: z.string().trim().min(1),
})

export type ShippingAddress = z.infer<typeof ShippingAddressSchema>

export type ShippingAddressField = keyof AddressOnFile

/** The claim's email: Zod's rule, as for the questionnaire that collects it. */
export const PortalEmailSchema = z.string().trim().email()

/** The fields, in form order, read off the schema rather than relisted. */
export const SHIPPING_ADDRESS_FIELDS = Object.keys(
  AddressOnFileSchema.shape,
) as ShippingAddressField[]

/** Required-ness also comes from the schema. */
export function isRequiredShippingAddressField(
  key: ShippingAddressField,
): boolean {
  return !ShippingAddressSchema.shape[key].isOptional()
}

/**
 * Name of the hidden input that carries this field on the claim POST.
 * Derived, so adding a field to the schema wires the request up with it.
 */
export function shippingAddressInputName(key: ShippingAddressField): string {
  return `address${key.charAt(0).toUpperCase()}${key.slice(1)}`
}

/**
 * Read an address out of untyped input — a Json column, a request body, a
 * row from the export query — keeping whatever is usable and dropping
 * blanks. Returns null when nothing is left, which is what makes the
 * portal's address window open in edit mode.
 */
export function toAddressOnFile(value: unknown): AddressOnFile | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null

  const parsed = AddressOnFileSchema.safeParse(value)
  if (!parsed.success) return null

  const filled = Object.entries(parsed.data).filter(
    ([, v]) => v !== undefined,
  ) as [keyof AddressOnFile, string][]

  return filled.length ? (Object.fromEntries(filled) as AddressOnFile) : null
}

/**
 * A complete, deliverable address, or null.
 *
 * The single gate for "may this physical claim go through", and it returns
 * the trimmed value so what gets stored is already normalised.
 */
export function toDeliverableAddress(value: unknown): ShippingAddress | null {
  const parsed = ShippingAddressSchema.safeParse(value)
  return parsed.success ? parsed.data : null
}

/**
 * The six flat `address*` fields off a claim request.
 *
 * `invalid` separates "no address was sent" — a valid digital claim — from
 * "an address was sent but it is not deliverable", which the caller turns
 * into the `address_required` message.
 */
export function parseShippingAddressBody(body: Record<string, unknown>): {
  address?: ShippingAddress
  invalid: boolean
} {
  const raw = Object.fromEntries(
    SHIPPING_ADDRESS_FIELDS.map((key) => [
      key,
      body[shippingAddressInputName(key)],
    ]),
  )

  const onRequest = toAddressOnFile(raw)
  if (!onRequest) return { invalid: false }

  const deliverable = toDeliverableAddress(onRequest)
  return deliverable
    ? { address: deliverable, invalid: false }
    : { invalid: true }
}

/** The address as display rows, blanks skipped. One definition of the order. */
export function shippingAddressLines(
  address: AddressOnFile | null | undefined,
): string[] {
  if (!address) return []
  return [
    address.name,
    [address.streetName, address.houseNumber].filter(Boolean).join(' '),
    [address.postcode, address.city].filter(Boolean).join(' '),
    address.country,
  ]
    .map((line) => (line || '').trim())
    .filter(Boolean)
}

export const ValidateResultSchema = z.object({
  voucherCodeId: z.string(),
  items: z.array(GiftItemSchema),
  email: z.string().nullable(),
  // Prefill for the address window — whatever is on file, however partial.
  address: AddressOnFileSchema.nullable(),
})

export type ValidateResult = z.infer<typeof ValidateResultSchema>

export const GiftPortalErrorKeySchema = z.enum([
  'invalid',
  'inactive',
  'redeemed',
  'expired',
  'address_required',
  'unknown',
])

export type GiftPortalErrorKey = z.infer<typeof GiftPortalErrorKeySchema>
